import { useEffect, useRef, useState } from 'react';

import { trackIndustrialAudioPlay } from '../industrialAnalytics';
import * as S from './IndustrialWaveformPlayer.styles';
import { loadSampleWaveform } from './industrialWaveformPeaks';
import { scheduleIdleTask } from './scheduleIdleTask';

type WaveSurferInstance = {
  destroy: () => void;
  on: (eventName: string, listener: () => void) => void;
  playPause: () => void;
};

interface Props {
  sampleId: string;
  src: string;
  transientSrc?: string;
  label: string;
  t: (key: string) => string;
}

export default function IndustrialWaveformPlayer({
  sampleId,
  src,
  transientSrc,
  label,
  t,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurferInstance | null>(null);
  const trackedPlayRef = useRef(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [waveformState, setWaveformState] = useState({
    src,
    isPlaying: false,
    isReady: false,
    hasError: false,
  });
  /*
   * Where the click is in this sample and how many times the playhead has
   * crossed it. The count is what restarts the one-shot animation: React
   * remounts the burst under a new key, and a fresh element runs its
   * keyframes from the start, which re-triggering a class cannot do.
   */
  const [clickMark, setClickMark] = useState<{ src: string; ratio: number }>();
  const [crossingCount, setCrossingCount] = useState(0);
  const currentState =
    waveformState.src === src
      ? waveformState
      : { src, isPlaying: false, isReady: false, hasError: false };
  const { hasError, isPlaying, isReady } = currentState;
  const playbackLabel = isPlaying ? t('Pause') : t('Play');
  const isConnectorClick = sampleId.startsWith('connector_click');
  const clickRatio = clickMark?.src === src ? clickMark.ratio : undefined;

  useEffect(() => {
    if (shouldLoad) {
      return undefined;
    }

    const target = containerRef.current;
    if (!target || typeof window === 'undefined') {
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      const timer = globalThis.setTimeout(() => setShouldLoad(true), 0);
      return () => globalThis.clearTimeout(timer);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '1600px 0px' },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) {
      return undefined;
    }

    let isMounted = true;
    const setupWaveform = async () => {
      try {
        const [{ default: WaveSurfer }, { peaks, duration, transientRatio }] =
          await Promise.all([
            import('wavesurfer.js'),
            loadSampleWaveform(src, transientSrc),
          ]);

        if (!isMounted || !containerRef.current) {
          return;
        }

        const wavesurfer = WaveSurfer.create({
          container: containerRef.current,
          url: src,
          peaks,
          duration,
          height: 46,
          normalize: true,
          cursorWidth: 0,
          interact: false,
          barWidth: 3,
          barGap: 3,
          barRadius: 3,
          waveColor: 'rgba(255, 255, 255, 0.62)',
          progressColor: '#ff3d59',
        });

        wavesurferRef.current = wavesurfer as WaveSurferInstance;

        const updateState = (
          update: Partial<Omit<typeof waveformState, 'src'>>,
        ) => {
          setWaveformState((previous) => ({
            ...(previous.src === src
              ? previous
              : { src, isPlaying: false, isReady: false, hasError: false }),
            ...update,
          }));
        };

        // Locate the click from its clean reference, not a factory transient.
        const ratio = isConnectorClick ? transientRatio : undefined;
        if (ratio !== undefined) {
          setClickMark({ src, ratio });
        }
        const clickAtSeconds =
          ratio !== undefined ? ratio * duration : undefined;
        let previousSeconds = 0;

        wavesurfer.on('timeupdate', (currentSeconds: number) => {
          // A time that went backwards means the clip restarted from its head
          const before = currentSeconds < previousSeconds ? 0 : previousSeconds;
          previousSeconds = currentSeconds;
          if (
            clickAtSeconds !== undefined &&
            before < clickAtSeconds &&
            currentSeconds >= clickAtSeconds
          ) {
            setCrossingCount((count) => count + 1);
          }
        });
        wavesurfer.on('ready', () => updateState({ isReady: true }));
        wavesurfer.on('play', () => {
          updateState({ isPlaying: true });
          if (!trackedPlayRef.current) {
            trackedPlayRef.current = true;
            trackIndustrialAudioPlay(sampleId);
          }
        });
        wavesurfer.on('pause', () => updateState({ isPlaying: false }));
        wavesurfer.on('finish', () => updateState({ isPlaying: false }));
        wavesurfer.on('error', () => updateState({ hasError: true }));
      } catch {
        if (isMounted) {
          setWaveformState({
            src,
            isPlaying: false,
            isReady: false,
            hasError: true,
          });
        }
      }
    };

    const cancelIdleTask = scheduleIdleTask(() => {
      void setupWaveform();
    });

    return () => {
      isMounted = false;
      cancelIdleTask();
      wavesurferRef.current?.destroy();
      wavesurferRef.current = null;
    };
  }, [isConnectorClick, sampleId, shouldLoad, src, transientSrc]);

  const togglePlayback = () => {
    if (!isReady || hasError) {
      return;
    }
    wavesurferRef.current?.playPause();
  };

  return (
    <div className={S.playerContainer}>
      <button
        type="button"
        className={S.playButton}
        onClick={togglePlayback}
        disabled={!isReady || hasError}
        aria-label={`${label} ${playbackLabel}`}
        aria-pressed={isPlaying}
      >
        {playbackLabel}
      </button>
      <div className={S.waveformCell}>
        <div ref={containerRef} className={S.waveformContainer} />
        {clickRatio !== undefined && (
          <span
            className={S.clickMarker}
            style={{ left: `${(clickRatio * 100).toFixed(3)}%` }}
            aria-hidden="true"
          >
            <span className={S.clickMarkerGuide} />
            {crossingCount > 0 && (
              <span key={crossingCount} className={S.clickMarkerFlash}>
                <span className={S.clickMarkerRing} />
              </span>
            )}
          </span>
        )}
      </div>
      {hasError && (
        <p className={S.errorText} role="status" aria-live="polite">
          {t('샘플을 불러오지 못했어요')}
        </p>
      )}
    </div>
  );
}
